<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  AcceptLibraryStepUpdateAcceptMutation,
  AcceptLibraryStepUpdateAcceptMutationVariables,
  AcceptLibraryStepUpdateDataQuery,
  AcceptLibraryStepUpdateDataQueryVariables,
  AcceptLibraryStepUpdateUsesQuery,
  AcceptLibraryStepUpdateUsesQueryVariables,
  PermissionOrganization,
  VersionStatus,
} from '@/generated/graphql'
import useAuthorization from '@/composables/useAuthorization'
import LibraryStepVersionChange from '@/components/step/LibraryStepVersionChange.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  stepId: string
  moduleId: string
  bopId: string
  isLatestBopVersion: boolean
}>()
const emit = defineEmits<{
  // The BOP that holds the update: this one, or the new draft of a release.
  updated: [bopId: string]
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()

const fetchQuery = useQuery<
  AcceptLibraryStepUpdateDataQuery,
  AcceptLibraryStepUpdateDataQueryVariables
>(
  gql`
    query AcceptLibraryStepUpdateData($id: ID!, $bopId: ID!) {
      product {
        step(id: $id) {
          id
          libraryStep {
            id
            name
            libraryItemVersion {
              libraryItemId
              version
              latestReleasedVersion
              updateAvailable
            }
          }
        }
        bop(id: $bopId) {
          id
          versionStatus
          productConfiguration {
            id
            archived
          }
        }
      }
    }
  `,
  () => ({ id: props.stepId, bopId: props.bopId }),
)

const libraryStep = computed(() => fetchQuery.result.value?.product.step.libraryStep)
const libraryItemVersion = computed(() => libraryStep.value?.libraryItemVersion)
const bop = computed(() => fetchQuery.result.value?.product.bop)
const isReleased = computed(() => bop.value?.versionStatus == VersionStatus.Released)
// Only the latest version takes the update: a draft in place, the latest release in a new draft.
const canUpdate = computed(
  () =>
    !!libraryItemVersion.value?.updateAvailable &&
    hasOrgAuthz(PermissionOrganization.OrgProductWrite) &&
    !bop.value?.productConfiguration.archived &&
    props.isLatestBopVersion,
)
const updateEffect = computed(() =>
  t(
    isReleased.value
      ? 'component.acceptLibraryStepUpdate.updateInNewDraft'
      : 'component.acceptLibraryStepUpdate.updateInDraft',
    { latest: libraryItemVersion.value?.latestReleasedVersion },
  ),
)

const isOpen = ref(false)
// The update moves every use of the step in this BOP, whatever version it is on.
const usesQuery = useQuery<
  AcceptLibraryStepUpdateUsesQuery,
  AcceptLibraryStepUpdateUsesQueryVariables
>(
  gql`
    query AcceptLibraryStepUpdateUses($bopId: ID!) {
      product {
        bop(id: $bopId) {
          id
          nodes {
            module {
              id
              name
              nodes {
                id
                step {
                  id
                  libraryStep {
                    id
                    libraryItemVersion {
                      libraryItemId
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId }),
  // Loaded with the Update button, so the dialog opens complete instead of filling in afterwards.
  () => ({ enabled: canUpdate.value }),
)
const modulesUsingStep = computed(() =>
  (usesQuery.result.value?.product.bop.nodes ?? [])
    .map((node) => node.module)
    .filter((module) =>
      module.nodes.some(
        (node) =>
          node.step.libraryStep?.libraryItemVersion?.libraryItemId ==
          libraryItemVersion.value?.libraryItemId,
      ),
    )
    .map((module) => module.name)
    .join(', '),
)

const acceptMutation = useMutation<
  AcceptLibraryStepUpdateAcceptMutation,
  AcceptLibraryStepUpdateAcceptMutationVariables
>(gql`
  mutation AcceptLibraryStepUpdateAccept($command: UpdateLibraryStepToLatestVersion!) {
    product {
      updateLibraryStepToLatestVersion(command: $command) {
        id
      }
    }
  }
`)
// A draft is updated in place and the steps keep their ids, so the step panel loads its content again;
// the module page reloads the step tree itself.
const stepPanelQueries = [
  'AcceptLibraryStepUpdateData',
  'LibraryStepLockStep',
  'ViewStepData',
  'ViewCheckConfigurationsData',
  'ArticleAllocationListData',
]
const accept = async () => {
  const result = await acceptMutation.mutate(
    { command: { moduleId: props.moduleId, stepId: props.stepId } },
    isReleased.value ? {} : { refetchQueries: stepPanelQueries, awaitRefetchQueries: true },
  )
  const updatedBopId = result?.data?.product.updateLibraryStepToLatestVersion.id
  isOpen.value = false
  if (updatedBopId) emit('updated', updatedBopId)
}
</script>

<template>
  <template v-if="canUpdate && libraryItemVersion">
    <v-dialog v-model="isOpen" width="480">
      <template #activator="{ props: activatorProps }">
        <v-btn v-bind="{ ...$attrs, ...activatorProps }">
          {{ t('component.acceptLibraryStepUpdate.buttonActivator') }}
        </v-btn>
      </template>
      <v-card :title="t('component.acceptLibraryStepUpdate.title')">
        <v-card-text>
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ libraryStep?.name }}</div>
          <library-step-version-change
            :current-version="libraryItemVersion.version"
            :new-version="libraryItemVersion.latestReleasedVersion"
            class="mb-4"
          />
          <p class="text-body-2">{{ updateEffect }}</p>
          <v-alert type="warning" variant="tonal" density="compact" class="mt-4">
            <!-- The theme's warning colour is too light for text (2.2:1); it stays on the icon. -->
            <div class="text-body-2 text-high-emphasis">
              {{ t('component.acceptLibraryStepUpdate.updateAffectsAllUses') }}
            </div>
            <div v-if="modulesUsingStep" class="text-body-2 text-high-emphasis mt-1">
              {{ t('component.acceptLibraryStepUpdate.usedIn', { modules: modulesUsingStep }) }}
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="isOpen = false">{{ t('button.cancel') }}</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="acceptMutation.loading.value"
            @click="accept"
          >
            {{ t('component.acceptLibraryStepUpdate.buttonAccept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
</template>
